import React, { useState } from 'react';
import { db, storage } from '../firebase/config';
import { collection, addDoc, doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { X, Star, Camera, Loader, AlertCircle } from 'lucide-react';

function ProductReview({ userInfo, productId, orderId, onClose, onSuccess }) {
    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [images, setImages] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);
        if (images.length + files.length > 5) {
            setError('이미지는 최대 5장까지 업로드할 수 있습니다.');
            return;
        }

        const validFiles = files.filter(file => {
            if (file.size > 5 * 1024 * 1024) {
                setError('각 이미지는 5MB 이하여야 합니다.');
                return false;
            }
            if (!file.type.startsWith('image/')) {
                setError('이미지 파일만 업로드 가능합니다.');
                return false;
            }
            return true;
        });

        setImages(prev => [...prev, ...validFiles]);

        validFiles.forEach(file => {
            const reader = new FileReader();
            reader.onload = (ev) => {
                setImagePreviews(prev => [...prev, ev.target.result]);
            };
            reader.readAsDataURL(file);
        });
    };

    const removeImage = (index) => {
        setImages(prev => prev.filter((_, i) => i !== index));
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        if (rating === 0) {
            setError('별점을 선택해주세요.');
            return;
        }
        if (!title.trim()) {
            setError('리뷰 제목을 입력해주세요.');
            return;
        }
        if (!content.trim()) {
            setError('리뷰 내용을 입력해주세요.');
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            // Upload images
            const uploadedUrls = [];
            for (const file of images) {
                const fileName = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${file.name}`;
                const storageRef = ref(storage, `productReviews/${productId}/${fileName}`);
                await uploadBytes(storageRef, file);
                const url = await getDownloadURL(storageRef);
                uploadedUrls.push(url);
            }

            // Get product info for sellerId
            const productRef = doc(db, 'marketplaceProducts', productId);
            const productSnap = await getDoc(productRef);
            const productData = productSnap.exists() ? productSnap.data() : {};
            const sellerId = productData.sellerId || '';

            // Create review document
            const reviewData = {
                productId,
                orderId,
                buyerId: userInfo.uid,
                buyerName: userInfo.name || userInfo.businessName || '익명',
                sellerId,
                rating,
                title: title.trim(),
                content: content.trim(),
                images: uploadedUrls,
                isVerifiedPurchase: true,
                helpfulCount: 0,
                status: 'active',
                createdAt: new Date(),
                updatedAt: new Date()
            };

            await addDoc(collection(db, 'productReviews'), reviewData);

            // Update product review stats (별도 try-catch: 구매자는 상품 수정 권한 없을 수 있음)
            if (productSnap.exists()) {
                try {
                    const currentReviewCount = productData.reviewCount || 0;
                    const currentAverageRating = productData.averageRating || 0;
                    const newReviewCount = currentReviewCount + 1;
                    const newAverageRating = ((currentAverageRating * currentReviewCount) + rating) / newReviewCount;

                    await updateDoc(productRef, {
                        reviewCount: newReviewCount,
                        averageRating: Math.round(newAverageRating * 10) / 10,
                        updatedAt: new Date()
                    });
                } catch (statsErr) {
                    console.warn('상품 평점 업데이트 스킵 (권한 없음):', statsErr.message);
                }
            }

            if (onSuccess) onSuccess();
            if (onClose) onClose();
        } catch (err) {
            console.error('리뷰 등록 실패:', err);
            setError('리뷰 등록에 실패했습니다. 다시 시도해주세요.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div style={styles.header}>
                    <div style={styles.headerLeft}>
                        <Star size={24} color="#6366f1" />
                        <h2 style={styles.headerTitle}>리뷰 작성</h2>
                    </div>
                    <button style={styles.closeBtn} onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div style={styles.body}>
                    {error && (
                        <div style={styles.errorBox}>
                            <AlertCircle size={18} color="#ef4444" />
                            <span>{error}</span>
                        </div>
                    )}

                    {/* Star Rating */}
                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>별점</label>
                        <div style={styles.starContainer}>
                            {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                    key={star}
                                    style={styles.starButton}
                                    onMouseEnter={() => setHoverRating(star)}
                                    onMouseLeave={() => setHoverRating(0)}
                                    onClick={() => setRating(star)}
                                >
                                    <Star
                                        size={36}
                                        color={(hoverRating || rating) >= star ? '#f59e0b' : '#e2e8f0'}
                                        fill={(hoverRating || rating) >= star ? '#f59e0b' : 'none'}
                                    />
                                </button>
                            ))}
                            <span style={styles.ratingText}>
                                {rating > 0 ? `${rating}점` : '선택해주세요'}
                            </span>
                        </div>
                    </div>

                    {/* Title */}
                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>제목</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="리뷰 제목을 입력해주세요"
                            style={styles.input}
                            maxLength={100}
                        />
                    </div>

                    {/* Content */}
                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>내용</label>
                        <textarea
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            placeholder="상품에 대한 솔직한 리뷰를 작성해주세요"
                            style={styles.textarea}
                            maxLength={1000}
                            rows={5}
                        />
                        <span style={styles.charCount}>{content.length}/1000</span>
                    </div>

                    {/* Image Upload */}
                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>사진 첨부 ({images.length}/5)</label>
                        <div style={styles.imageUploadArea}>
                            {imagePreviews.map((preview, index) => (
                                <div key={index} style={styles.imagePreviewWrapper}>
                                    <img src={preview} alt={`preview-${index}`} style={styles.imagePreview} />
                                    <button
                                        style={styles.imageRemoveBtn}
                                        onClick={() => removeImage(index)}
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            ))}
                            {images.length < 5 && (
                                <label style={styles.imageAddBtn}>
                                    <Camera size={24} color="#94a3b8" />
                                    <span style={styles.imageAddText}>추가</span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={handleImageUpload}
                                        style={{ display: 'none' }}
                                    />
                                </label>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div style={styles.footer}>
                    <button style={styles.cancelBtn} onClick={onClose} disabled={submitting}>
                        취소
                    </button>
                    <button
                        style={{
                            ...styles.submitBtn,
                            ...(submitting ? styles.submitBtnDisabled : {})
                        }}
                        onClick={handleSubmit}
                        disabled={submitting}
                    >
                        {submitting ? (
                            <>
                                <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                                등록 중...
                            </>
                        ) : (
                            '리뷰 등록'
                        )}
                    </button>
                </div>
            </div>

            <style>{`
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
}

const styles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        zIndex: 1000
    },
    modal: {
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 24px',
        borderBottom: '1px solid #e2e8f0'
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    headerTitle: {
        margin: 0,
        fontSize: '20px',
        fontWeight: '700',
        color: '#0f172a'
    },
    closeBtn: {
        width: '40px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f1f5f9',
        border: 'none',
        borderRadius: '10px',
        color: '#64748b',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    body: {
        flex: 1,
        overflowY: 'auto',
        padding: '24px'
    },
    errorBox: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 16px',
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '10px',
        fontSize: '14px',
        color: '#ef4444',
        marginBottom: '20px'
    },
    fieldGroup: {
        marginBottom: '20px'
    },
    label: {
        display: 'block',
        fontSize: '14px',
        fontWeight: '600',
        color: '#374151',
        marginBottom: '8px'
    },
    starContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
    },
    starButton: {
        background: 'none',
        border: 'none',
        padding: '2px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        transition: 'transform 0.15s'
    },
    ratingText: {
        marginLeft: '12px',
        fontSize: '16px',
        fontWeight: '600',
        color: '#f59e0b'
    },
    input: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '15px',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        outline: 'none',
        color: '#0f172a',
        backgroundColor: '#f8fafc',
        boxSizing: 'border-box',
        transition: 'border-color 0.2s'
    },
    textarea: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '15px',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        outline: 'none',
        color: '#0f172a',
        backgroundColor: '#f8fafc',
        boxSizing: 'border-box',
        resize: 'vertical',
        fontFamily: 'inherit',
        lineHeight: '1.6',
        transition: 'border-color 0.2s'
    },
    charCount: {
        display: 'block',
        textAlign: 'right',
        marginTop: '4px',
        fontSize: '12px',
        color: '#94a3b8'
    },
    imageUploadArea: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px'
    },
    imagePreviewWrapper: {
        position: 'relative',
        width: '80px',
        height: '80px',
        borderRadius: '10px',
        overflow: 'hidden',
        border: '1px solid #e2e8f0'
    },
    imagePreview: {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },
    imageRemoveBtn: {
        position: 'absolute',
        top: '4px',
        right: '4px',
        width: '22px',
        height: '22px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.6)',
        border: 'none',
        borderRadius: '50%',
        color: '#ffffff',
        cursor: 'pointer',
        padding: 0
    },
    imageAddBtn: {
        width: '80px',
        height: '80px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px',
        border: '2px dashed #cbd5e1',
        borderRadius: '10px',
        backgroundColor: '#f8fafc',
        cursor: 'pointer',
        transition: 'border-color 0.2s'
    },
    imageAddText: {
        fontSize: '11px',
        color: '#94a3b8',
        fontWeight: '500'
    },
    footer: {
        display: 'flex',
        gap: '12px',
        padding: '20px 24px',
        borderTop: '1px solid #e2e8f0'
    },
    cancelBtn: {
        flex: 1,
        padding: '14px',
        backgroundColor: '#f1f5f9',
        border: 'none',
        borderRadius: '10px',
        fontSize: '15px',
        fontWeight: '600',
        color: '#64748b',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    submitBtn: {
        flex: 1,
        padding: '14px',
        backgroundColor: '#6366f1',
        border: 'none',
        borderRadius: '10px',
        fontSize: '15px',
        fontWeight: '600',
        color: '#ffffff',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        transition: 'all 0.2s'
    },
    submitBtnDisabled: {
        backgroundColor: '#a5b4fc',
        cursor: 'not-allowed'
    }
};

export default ProductReview;
